/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, September 8th 2022, 3:52:28 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { Node, Edge, Viewport, useNodesState, useEdgesState, OnNodesChange, OnEdgesChange } from "react-flow-renderer";
import { BoundaryEventConfiguration, TaskTemplate } from "lib/schemas";
import { IFlowChartAdapter } from "service-builder/adapters/types";
import { useServiceBuilderContext } from "service-builder/providers";
import { BOUNDARY_EVENT_NODE_SIZE } from "./constants";

export type IReactFlowNodeData = { taskTemplate: TaskTemplate; boundaryEvent?: BoundaryEventConfiguration };

interface IReactFlowContext {
  nodes: Node<IReactFlowNodeData>[];
  edges: Edge<TaskTemplate>[];
  defaultViewportState: Viewport;
  setNodes: React.Dispatch<React.SetStateAction<Node<IReactFlowNodeData>[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge<TaskTemplate>[]>>;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  persistViewPortState: (viewport: Viewport) => void;
  persistNodePosition: (args: { taskTemplate: TaskTemplate; x: number; y: number }) => void;
}

export const ReactFlowContext = React.createContext<IReactFlowContext>({
  nodes: [],
  edges: [],
  defaultViewportState: null,
  setNodes: () => {},
  setEdges: () => {},
  onNodesChange: (changes) => {},
  onEdgesChange: (changes) => {},
  persistViewPortState: () => {},
  persistNodePosition: () => {},
});

export const useReactFlowContext = () => {
  return React.useContext(ReactFlowContext);
};

export const useReactFlowAdapter: IFlowChartAdapter<IReactFlowContext> = ({
  taskTemplateListContext: { filteredItems, updateList, findItem },
  patchTaskTemplate,
}) => {
  const {
    processDefinitionContext: { currentProcessDefinition },
    patchCurrentProcessDefinition,
  } = useServiceBuilderContext();

  const [nodes, setNodes, onNodesChange] = useNodesState(reactFlowNodesAdapter(filteredItems));
  const [edges, setEdges, onEdgesChange] = useEdgesState(reactFlowEdgesAdapter(filteredItems));

  React.useEffect(() => {
    setNodes((prevNodes) => reactFlowNodesAdapter(filteredItems, prevNodes));
    setEdges(reactFlowEdgesAdapter(filteredItems));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filteredItems)]);

  const persistViewPortState = React.useCallback<IReactFlowContext["persistViewPortState"]>(
    (viewport: Viewport) => {
      const [x_position_factor, y_position_factor] = ElementPositionFactor.toFactor(viewport?.x, viewport?.y);

      patchCurrentProcessDefinition({
        extra_data: {
          ...currentProcessDefinition?.extra_data,
          flowchart_viewport_state: { x_position_factor, y_position_factor, zoom_level: 1 },
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(currentProcessDefinition?.extra_data)]
  );

  const getDefaultViewportState = (): Viewport => {
    const [x, y] = ElementPositionFactor.toPosition(
      currentProcessDefinition?.extra_data?.flowchart_viewport_state?.x_position_factor,
      currentProcessDefinition?.extra_data?.flowchart_viewport_state?.y_position_factor
    );

    return {
      x,
      y,
      zoom: currentProcessDefinition?.extra_data?.flowchart_viewport_state?.zoom_level,
    };
  };

  const persistNodePosition = React.useCallback<IReactFlowContext["persistNodePosition"]>(
    ({ taskTemplate, x, y }) => {
      if (!taskTemplate?.id) return;
      patchTaskTemplate({
        apiPathParams: [taskTemplate.id],
        body: JSON.stringify({
          extra_data: {
            ...taskTemplate?.extra_data,
            flowchart_position: { x_position_factor: x, y_position_factor: y },
          },
        }),
        setValidAPIResponse: (apiResponse) => updateList(apiResponse),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    nodes,
    edges,
    defaultViewportState: getDefaultViewportState(),
    setNodes,
    onNodesChange,
    setEdges,
    onEdgesChange,
    persistViewPortState,
    persistNodePosition,
  };
};

const ElementPositionFactor = {
  toFactor: (elementXPosition: number, elementYPosition: number) => {
    return [elementXPosition / window.innerWidth, elementYPosition / window.innerHeight];
  },
  toPosition: (elementXPositionFactor: number, elementYPositionFactor: number) => {
    return [elementXPositionFactor ?? 0 * window.innerWidth, elementYPositionFactor ?? 0 * window.innerHeight];
  },
};

const reactFlowNodesAdapter = (
  taskTemplates: TaskTemplate[],
  reactFlowNodes?: Node<IReactFlowNodeData>[]
): Node<IReactFlowNodeData>[] => {
  let xPosition: number;
  let yPosition: number;
  let previousSplitNode: TaskTemplate;
  let outgoingNodeXFactor: number = -5;
  const computedNodes = [];
  taskTemplates
    .filter((taskTemplate) => taskTemplate.enabled)
    .forEach((taskTemplate) => {
      const { x_position_factor, y_position_factor } = taskTemplate?.extra_data?.flowchart_position ?? {};

      const existingReactFlowNode = reactFlowNodes?.find((node) => node.data.taskTemplate.id === taskTemplate.id);

      if (existingReactFlowNode?.position) {
        xPosition = existingReactFlowNode?.position?.x;
        yPosition = existingReactFlowNode?.position?.y;
      } else if (x_position_factor && y_position_factor) {
        xPosition = x_position_factor;
        yPosition = y_position_factor;
      } else if (
        previousSplitNode?.type === "Split" &&
        previousSplitNode?.outgoing_branches
          .map((outgoing_summary) => outgoing_summary.next_id)
          .includes(taskTemplate.id)
      ) {
        outgoingNodeXFactor = outgoingNodeXFactor + 5;
        xPosition = 125 * outgoingNodeXFactor;
        yPosition = (previousSplitNode.sequence + 1) * 100;
      } else {
        xPosition = 250;
        outgoingNodeXFactor = -5;
        yPosition = taskTemplate.sequence * 100;
      }

      if (taskTemplate.type === "Split") {
        previousSplitNode = taskTemplate;
      }

      computedNodes.push({
        id: `${taskTemplate.id}`,
        data: { taskTemplate },
        type: "mainSequenceFlowNode",
        position: { x: xPosition, y: yPosition },
      });

      taskTemplate.boundary_events_detailed?.forEach((boundaryEvent, index) => {
        if (!!!boundaryEvent) return;
        computedNodes.push({
          id: getBoundaryEventNodeId(boundaryEvent),
          data: { taskTemplate, boundaryEvent },
          type: "boundaryEventFlowNode",
          position: { x: 15 + (BOUNDARY_EVENT_NODE_SIZE + 5) * index, y: 48 }, // TODO: Make the y position dynamic by making using absolute on child
          parentNode: `${taskTemplate.id}`, // https://stackoverflow.com/questions/78099676/dragging-a-node-with-its-children-in-react-flow
          extent: "parent", // https://reactflow.dev/learn/layouting/sub-flows#adding-child-nodes
          draggable: false,
        });
      });
    });

  return computedNodes;
};

const reactFlowEdgesAdapter = (taskTemplates: TaskTemplate[]): Edge<TaskTemplate>[] => {
  return taskTemplates.reduce((cumulativeEdgesList: Edge<TaskTemplate>[], currentTaskTemplate: TaskTemplate) => {
    let currentEdgesList: Edge<TaskTemplate>[] = [];

    switch (currentTaskTemplate.type) {
      case "Split":
        currentEdgesList = currentEdgesList.concat(getOutgoingEdges(currentTaskTemplate));
        break;
      default:
        currentEdgesList = currentEdgesList.concat([getDirectEdge(currentTaskTemplate)]);
    }
    currentEdgesList = currentEdgesList.concat(getBoundaryEventEdges(currentTaskTemplate));

    return cumulativeEdgesList.concat(currentEdgesList);
  }, []);
};

const getOutgoingEdges = (gatewayNodeTemplate: TaskTemplate) =>
  gatewayNodeTemplate.outgoing_branches?.map((outgoing_summary) => ({
    id: `${gatewayNodeTemplate.id}-${outgoing_summary.next_id}`,
    source: `${gatewayNodeTemplate.id}`,
    target: `${outgoing_summary.next_id}`,
    type: "taskTemplateNodeEdge",
  }));

const getDirectEdge = (taskTemplate: TaskTemplate) => ({
  id: `${taskTemplate.id}-${taskTemplate.next}`,
  source: `${taskTemplate.id}`,
  target: `${taskTemplate.next}`,
  type: "taskTemplateNodeEdge",
});

const getBoundaryEventEdges = (hostNodeTemplate: TaskTemplate) =>
  hostNodeTemplate?.boundary_events_detailed?.map((boundaryEvent) => {
    const boundaryEventNodeId = getBoundaryEventNodeId(boundaryEvent);
    return {
      id: `${boundaryEventNodeId}-${boundaryEvent.target_node}`,
      source: boundaryEventNodeId,
      target: `${boundaryEvent.target_node}`,
      type: "boundaryEventNodeEdge",
      style: { strokeDasharray: "10" },
    };
  }) ?? [];

export const getBoundaryEventNodeId = (boundaryEvent: BoundaryEventConfiguration) => {
  return `boundary_${boundaryEvent.id}_host_${boundaryEvent.host_node}`;
};
